/**
 * PatientDemographicsPanel - Editable patient demographics form
 *
 * Displays and allows editing of basic patient information:
 * name fields, DOB, gender/sex, pronouns, phone, email
 *
 * Uses RTK Query for updates with automatic cache invalidation.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdatePatientMutation } from '@features/patients';
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { Patient } from '@ts/types';
import { validateDob, validateEmail, validatePhone } from '@utils/patientValidation';
import { useSnackbar } from 'notistack';

interface PatientDemographicsPanelProps {
  patient: Patient | null;
  isLoading?: boolean;
  onPatientUpdate?: (field: string, value: string) => void;
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const PRONOUNS_OPTIONS = [
  { value: 'he/him', label: 'He/Him' },
  { value: 'she/her', label: 'She/Her' },
  { value: 'they/them', label: 'They/Them' },
  { value: 'other', label: 'Other' },
];

export const PatientDemographicsPanel: React.FC<PatientDemographicsPanelProps> = ({
  patient,
  isLoading = false,
  onPatientUpdate,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation();

  // Local state for form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});

  // Track the previous patient ID to detect when we switch patients
  // This prevents resetting form fields on cache refetch (which would cause race conditions)
  const previousPatientIdRef = useRef<string | null>(null);

  // Initialize form from patient data - only when switching to a different patient
  // This prevents the race condition where saving one field triggers a cache refetch,
  // which would reset other fields the user is actively editing.
  useEffect(() => {
    const currentPatientId = patient?.patient_id ?? null;
    const previousPatientId = previousPatientIdRef.current;

    // Only reset form fields when switching to a different patient
    if (currentPatientId !== previousPatientId) {
      previousPatientIdRef.current = currentPatientId;

      if (patient) {
        setFirstName(patient.first_name || '');
        setLastName(patient.last_name || '');
        setPreferredName(patient.preferred_name || '');
        setDob(patient.dob || '');
        setGender(patient.gender || '');
        setPronouns(patient.pronouns || '');
        setPhone(patient.mobile_phone || patient.phone || '');
        setEmail(patient.email || '');
      }
    }
  }, [patient]);

  // Validate field before saving
  const validateField = useCallback((field: string, value: string): boolean => {
    let validation: { isValid: boolean; error?: string };

    switch (field) {
      case 'mobile_phone':
      case 'phone':
        validation = validatePhone(value);
        break;
      case 'email':
        validation = validateEmail(value);
        break;
      case 'dob':
        validation = validateDob(value);
        break;
      default:
        validation = { isValid: true };
    }

    if (!validation.isValid && validation.error) {
      setFieldErrors(prev => ({ ...prev, [field]: validation.error || null }));
      return false;
    }

    setFieldErrors(prev => ({ ...prev, [field]: null }));
    return true;
  }, []);

  // Get display name for field (for success notifications)
  const getFieldDisplayName = (field: string): string => {
    switch (field) {
      case 'first_name':
        return 'First name';
      case 'last_name':
        return 'Last name';
      case 'preferred_name':
        return 'Preferred name';
      case 'mobile_phone':
      case 'phone':
        return 'Phone number';
      case 'email':
        return 'Email address';
      case 'dob':
        return 'Date of birth';
      case 'gender':
        return 'Gender';
      case 'pronouns':
        return 'Pronouns';
      default:
        return field;
    }
  };

  // Update patient field via RTK Query mutation
  const handleFieldUpdate = useCallback(
    async (field: string, value: string, originalValue: string) => {
      if (value === originalValue || !patient?.patient_id) return;

      // Validate first
      if (!validateField(field, value)) return;

      // Format phone numbers (remove spaces for backend)
      let formattedValue = value;
      if (field === 'mobile_phone' || field === 'phone') {
        formattedValue = value.replace(/\s+/g, '');
      }

      try {
        await updatePatient({
          patientId: patient.patient_id,
          updatePatientRequest: {
            [field]: formattedValue,
          },
        }).unwrap();

        enqueueSnackbar(`${getFieldDisplayName(field)} saved`, { variant: 'success' });
        onPatientUpdate?.(field, value);
      } catch (error) {
        console.error(`Error updating ${field}:`, error);
        enqueueSnackbar(`Failed to save ${getFieldDisplayName(field).toLowerCase()}`, {
          variant: 'error',
        });
      }
    },
    [patient?.patient_id, validateField, updatePatient, enqueueSnackbar, onPatientUpdate],
  );

  // Handle blur events to save changes
  const handleBlur = (field: string, value: string, originalValue: string) => {
    void handleFieldUpdate(field, value, originalValue);
  };

  // Handle select changes (save immediately)
  const handleSelectChange = (field: string, value: string, originalValue: string) => {
    void handleFieldUpdate(field, value, originalValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!patient) {
    return (
      <Typography variant="body2" color="text.secondary">
        No patient selected.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2}>
        {/* Name Fields Row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="First Name"
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            onBlur={() => handleBlur('first_name', firstName, patient.first_name || '')}
            size="small"
            fullWidth
            error={!!fieldErrors.first_name}
            helperText={fieldErrors.first_name}
            disabled={isUpdating}
          />
          <TextField
            label="Last Name"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            onBlur={() => handleBlur('last_name', lastName, patient.last_name || '')}
            size="small"
            fullWidth
            error={!!fieldErrors.last_name}
            helperText={fieldErrors.last_name}
            disabled={isUpdating}
          />
          <TextField
            label="Preferred Name"
            value={preferredName}
            onChange={e => setPreferredName(e.target.value)}
            onBlur={() => handleBlur('preferred_name', preferredName, patient.preferred_name || '')}
            size="small"
            fullWidth
            placeholder="Optional"
            disabled={isUpdating}
          />
        </Stack>

        {/* DOB and Gender/Pronouns Row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Date of Birth"
            type="date"
            value={dob}
            onChange={e => setDob(e.target.value)}
            onBlur={() => handleBlur('dob', dob, patient.dob || '')}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split('T')[0],
            }}
            error={!!fieldErrors.dob}
            helperText={fieldErrors.dob}
            disabled={isUpdating}
          />
          <FormControl size="small" fullWidth disabled={isUpdating}>
            <InputLabel>Gender</InputLabel>
            <Select
              value={gender}
              label="Gender"
              onChange={e => {
                const newValue = e.target.value;
                setGender(newValue);
                handleSelectChange('gender', newValue, patient.gender || '');
              }}
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {GENDER_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth disabled={isUpdating}>
            <InputLabel>Pronouns</InputLabel>
            <Select
              value={pronouns}
              label="Pronouns"
              onChange={e => {
                const newValue = e.target.value;
                setPronouns(newValue);
                handleSelectChange('pronouns', newValue, patient.pronouns || '');
              }}
            >
              <MenuItem value="">
                <em>Not specified</em>
              </MenuItem>
              {PRONOUNS_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Contact Info Row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Phone"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            onBlur={() =>
              handleBlur('mobile_phone', phone, patient.mobile_phone || patient.phone || '')
            }
            size="small"
            fullWidth
            placeholder="+1 555 123 4567"
            error={!!fieldErrors.mobile_phone || !!fieldErrors.phone}
            helperText={fieldErrors.mobile_phone || fieldErrors.phone}
            disabled={isUpdating}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            onBlur={() => handleBlur('email', email, patient.email || '')}
            size="small"
            fullWidth
            placeholder="patient@example.com"
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            disabled={isUpdating}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
